import { useEffect, useState, useCallback, useRef, useMemo, useSyncExternalStore } from 'react';
import { apiFetch, getToken, STREAM_API } from '../../utils/streamApi';
import { makeSimpleRing } from '../../utils/polygon';
import IndustrialLayout, { toast } from './IndustrialLayout';
// The report rendering and the small UI pieces are shared with the scheduled
// Shop Analytics Report, so both screens define every figure once.
import { Report, Thumb, Field, Stat, secs, sel } from '../../components/AnalyticsReport';

// ── Video Analyzer ────────────────────────────────────────────────────────────
// Upload a recording from a customer's own shop, mark up where the door, the shop
// floor, the counter and the shelves are, and get back the numbers they asked to
// see proved: customers in and out, time spent per unique customer, staff time
// behind the counter, shelf reaches — each with the frame it came from.
//
// Deliberately NOT a live page. It never reads a camera and never writes camera
// config; the backend runs the models over the file offline (video_analytics.py).
//
// The geometry is optional, and the report says what each missing shape cost:
// with no entrance line it still reports unique visitors and time spent, it just
// cannot say in vs out. That matters because the first thing anyone does with this
// page is drop in a clip and press Run before reading anything.

const DRAW = [
  { key: 'line',    label: 'Counting line',
    hint: 'Two clicks. Across the doorway it counts people entering and leaving the shop. Across the front of the counter it counts customers stepping up to be served and stepping away — so a camera with no door in view still counts. Pick which below.',
    color: '#22c55e', shape: 'line' },
  { key: 'shop',    label: 'Shop floor',    hint: 'Time spent is measured inside this area. Keeps people visible through the door out of the figures.',
    color: '#38bdf8', shape: 'poly' },
  { key: 'counter', label: 'Behind counter', hint: 'The staff side. Anyone who spends 3+ seconds here is staff for the whole clip and is never counted as a customer — and their time behind the counter is reported. Usually the only staff shape you need.',
    color: '#f59e0b', shape: 'poly' },
  { key: 'staff',   label: 'Staff area (extra)', hint: 'Only for places staff go that are NOT the counter — a back room or dispensary. Anyone spending 3+ seconds here is staff for the whole clip, so do not draw it over floor a customer can stand on.',
    color: '#a855f7', shape: 'poly', multi: true },
  { key: 'service', label: 'At the counter', hint: 'The customer side, where someone stands to be served. Gives time per customer at the counter and how many were served.',
    color: '#fb7185', shape: 'poly' },
  { key: 'shelf',   label: 'Shelf',         hint: 'Draw the shelf FACE itself — the door or glass front — not the counter or floor in front of it. Each open-then-close is reported once, with how long it stood open.',
    color: '#facc15', shape: 'poly', multi: true },
];

// ── upload lives OUTSIDE the component ────────────────────────────────────────
// An upload here is hundreds of MB and takes minutes. Held in component state it
// behaved badly in two ways: leaving the page lost the progress readout for good
// (the request itself carried on, so the job appeared out of nowhere in the list
// later), and a reload killed the transfer with no warning, orphaning a partial
// file on the server. Module scope means the transfer survives navigation and a
// remount re-attaches to it, and `beforeunload` covers the reload case.
const CHUNK = 8 << 20;        // 8MB parts — well under the proxy's 50M body cap
let _up = null;               // { pct, name, job, error, cancel, id }
const _upSubs = new Set();
const _upEmit = () => _upSubs.forEach(fn => fn());
const _upSub = (cb) => { _upSubs.add(cb); return () => _upSubs.delete(cb); };
const _upSnap = () => _up;

function _guardUnload(e) {
  e.preventDefault();
  e.returnValue = '';          // browsers show their own wording
  return '';
}

// "23 Sep 00:31" — short enough for a dropdown row, and unambiguous between
// several runs of the same file on the same day, which is the whole point.
const when = (epoch) => {
  if (!epoch) return '';
  try {
    return new Date(epoch * 1000).toLocaleString(undefined, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};


export default function IndustrialVideoAnalyzerPage() {
  const [token, setToken]   = useState('');
  const [jobs, setJobs]     = useState([]);
  const [listErr, setListErr] = useState(null);
  const [job, setJob]       = useState(null);      // the selected job (full record)
  const [busy, setBusy]     = useState(false);
  // Upload state comes from the module store, so it survives leaving the page.
  const up = useSyncExternalStore(_upSub, _upSnap, _upSnap);

  // geometry being drawn
  const [mode, setMode]     = useState('line');
  const [pts, setPts]       = useState([]);
  const [geo, setGeo]       = useState({ line: null, in_side: 1, purpose: 'entrance',
                                         shop: null, staff: [], counter: null,
                                         service: null, shelf: [] });
  // run knobs
  const [fps, setFps]       = useState(8);
  const [maxSecs, setMax]   = useState(0);
  const [doShelf, setShelf] = useState(true);
  const [doFaces, setFaces] = useState(true);
  const [minVisit, setMinV] = useState(3);

  const [lightbox, setLightbox] = useState(null);  // {src, caption}
  // Markup at full screen. Drawing a door line to within a few percent of frame
  // width is the difference between 2 entries and 3, and a 900px-wide still makes
  // that hard to do by eye.
  const [big, setBig] = useState(false);
  // Which second of the clip the markup frame is showing. null = the default
  // still. Needed because a shelf has to be marked at a moment it is OPEN, and
  // that may be anywhere in the recording.
  const [frameT, setFrameT] = useState(null);

  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const fileRef   = useRef(null);

  const spec    = DRAW.find(d => d.key === mode);
  const results = job?.results;
  const running = job && (job.status === 'running' || job.status === 'queued');

  useEffect(() => { getToken().then(setToken).catch(() => {}); }, []);

  // Esc leaves full screen. Without it the only way out is the button, which is
  // easy to lose track of once the frame fills the viewport.
  useEffect(() => {
    if (!big) return;
    const onKey = (e) => { if (e.key === 'Escape') setBig(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [big]);

  // Failures are SURFACED, not swallowed. A 403 from a non-admin login and an
  // empty list rendered identically before this, so the page looked like a fresh
  // install when it was actually being refused.
  const loadJobs = useCallback(() => {
    apiFetch('/ui/analyze/jobs')
      .then(async r => {
        if (!r.ok) {
          let detail = '';
          try { detail = (await r.json()).detail || ''; } catch { /* no body */ }
          setListErr(r.status === 403
            ? 'This page needs an admin login — the server refused the request (403).'
            : `Could not read the analysis list (HTTP ${r.status}${detail ? `: ${detail}` : ''}).`);
          return null;
        }
        setListErr(null);
        return r.json();
      })
      .then(d => { if (d) setJobs(d.jobs || []); })
      .catch(() => setListErr('Could not reach the server.'));
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Adopt an upload that completed while this page was unmounted. Without this the
  // job exists on the server but the person who started it is never shown it.
  useEffect(() => {
    if (!up?.job) return;
    setJob(up.job);
    setPts([]);
    setGeo({ line: null, in_side: 1, purpose: 'entrance', shop: null, staff: [],
             counter: null, service: null, shelf: [] });
    _up = null;
    _upEmit();
    loadJobs();
  }, [up?.job, loadJobs]);

  // The analysed frame, re-fetched while a job runs. The backend writes one about
  // once per second of video, so 1.5s here keeps it moving without thrashing.
  const [liveUrl, setLiveUrl] = useState(null);
  useEffect(() => {
    if (!job?.id || !running || !token) { setLiveUrl(null); return; }
    const tick = () => setLiveUrl(
      `${STREAM_API}/ui/analyze/${job.id}/live?token=${token}&t=${Date.now()}`);
    tick();
    const iv = setInterval(tick, 1500);
    return () => clearInterval(iv);
  }, [job?.id, running, token]);

  // Poll the selected job while it is working. 2s: the backend writes progress
  // every 20 samples, so faster polling would just re-read the same numbers.
  useEffect(() => {
    if (!job?.id || !running) return;
    const tick = () => apiFetch(`/ui/analyze/${job.id}`).then(r => r.json())
      .then(j => { setJob(j); if (j.status !== 'running' && j.status !== 'queued') loadJobs(); })
      .catch(() => {});
    const iv = setInterval(tick, 2000);
    return () => clearInterval(iv);
  }, [job?.id, running, loadJobs]);

  const openJob = (id) => {
    apiFetch(`/ui/analyze/${id}`).then(r => r.json()).then(j => {
      setJob(j);
      setPts([]);
      const p = j.params || {};
      // Re-open a finished job with the geometry it was actually run with, so the
      // canvas explains the numbers underneath it rather than whatever was last drawn.
      if (p.entry_line || p.shop_zone || (p.staff_zones || []).length) {
        setGeo({ line: p.entry_line || null, in_side: p.in_side ?? 1,
                 purpose: p.line_purpose || 'entrance',
                 shop: p.shop_zone || null, staff: p.staff_zones || [],
                 counter: p.counter_zone || null, service: p.service_zone || null,
                 shelf: (p.shelf_zones || []).map(s => ({ poly: s.polygon || s,
                                                          open_at: s.open_at ?? null })) });
      }
      if (p.analysis_fps) setFps(p.analysis_fps);
    }).catch(() => {});
  };

  // Re-read the selected job and the job list on demand. Keeps whatever geometry
  // is on the canvas: pressing Refresh mid-markup must not discard the shapes.
  const refresh = () => {
    loadJobs();
    if (!job?.id) { toast('Reloaded the list.', 'ok'); return; }
    apiFetch(`/ui/analyze/${job.id}`).then(r => r.json()).then(j => {
      setJob(j);
      toast(j.status === 'done' ? 'Report is ready.'
        : j.status === 'running' ? `Still analysing — ${j.progress?.pct || 0}%.`
          : `Status: ${j.status}.`, j.status === 'error' ? 'err' : 'ok');
    }).catch(() => toast('Could not reach the server.', 'err'));
  };

  // ── upload (XHR, not fetch — fetch cannot report upload progress and these
  //    files are hundreds of MB) ───────────────────────────────────────────────
  // Sent in PARTS, never as one big POST. nginx in front of the API caps a body
  // at 50M and the router buffers a whole body in memory, so a real NVR export
  // sent whole dies with a connection reset. 8MB parts stay under the cap, keep
  // upstream memory flat, and let a failed part be retried instead of the file.
  const upload = async (file) => {
    if (!file) return;
    if (_up) { toast('An upload is already running.', 'warn'); return; }

    _up = { pct: 0, name: file.name, job: null, error: null, cancel: false, id: null };
    _upEmit();
    window.addEventListener('beforeunload', _guardUnload);

    const fail = (msg) => { _up = { ..._up, error: msg }; toast(`Upload failed: ${msg}`, 'err'); };
    const detailOf = async (r) => {
      try { return (await r.json()).detail || `HTTP ${r.status}`; } catch { return `HTTP ${r.status}`; }
    };

    try {
      const b = await apiFetch('/ui/analyze/upload/begin',
        { method: 'POST', json: { filename: file.name } });
      if (!b.ok) { fail(await detailOf(b)); return; }
      const { upload_id } = await b.json();
      _up.id = upload_id;

      let off = 0;
      while (off < file.size) {
        if (_up?.cancel) {
          await apiFetch('/ui/analyze/upload/abort',
            { method: 'POST', json: { upload_id } }).catch(() => {});
          _up = null; toast('Upload cancelled.', 'warn');
          return;
        }
        const part = file.slice(off, Math.min(off + CHUNK, file.size));
        // Three tries per part, backing off. A dropped part on a slow link used to
        // mean starting the whole file again.
        let sent = null, last = '';
        for (let attempt = 0; attempt < 3 && sent === null; attempt++) {
          try {
            const r = await apiFetch(
              `/ui/analyze/upload/part?upload_id=${encodeURIComponent(upload_id)}&offset=${off}`,
              { method: 'POST', body: part });
            if (!r.ok) { last = await detailOf(r); }
            else { sent = (await r.json()).size; }
          } catch { last = 'the connection dropped mid-upload'; }
          if (sent === null) await new Promise(res => setTimeout(res, 600 * (attempt + 1)));
        }
        if (sent === null) { fail(last || 'a part could not be sent'); return; }
        off = sent;
        _up = { ..._up, pct: Math.round((off / file.size) * 100) };
        _upEmit();
      }

      const f = await apiFetch('/ui/analyze/upload/finish',
        { method: 'POST', json: { upload_id, filename: file.name, size: file.size } });
      if (!f.ok) { fail(await detailOf(f)); return; }
      const j = await f.json();
      // Parked on the module object, not component state, so a remount after
      // navigating away still finds the finished job instead of losing it.
      _up = { ..._up, pct: 100, job: j };
      toast(`Uploaded — ${j.video.width}×${j.video.height}, ${secs(j.video.duration)}.`, 'ok');
    } catch (e) {
      fail(e?.message || 'unexpected error');
    } finally {
      window.removeEventListener('beforeunload', _guardUnload);
      _upEmit();
    }
  };

  const cancelUpload = () => {
    if (_up) { _up = { ..._up, cancel: true }; _upEmit(); }
  };

  // ── canvas ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const cv = canvasRef.current, im = imgRef.current;
    if (!cv || !im || !im.complete) return;
    const W = cv.width, H = cv.height, ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    const dot = (p, c) => { ctx.fillStyle = c; ctx.beginPath(); ctx.arc(p[0] * W, p[1] * H, 5, 0, 7); ctx.fill(); };
    const poly = (pl, c, label) => {
      if (!pl || pl.length < 3) return;
      ctx.strokeStyle = c; ctx.fillStyle = c + '33'; ctx.lineWidth = 2;
      ctx.beginPath(); pl.forEach((p, i) => i ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.closePath(); ctx.fill(); ctx.stroke();
      pl.forEach(p => dot(p, c));
      if (label) {
        ctx.fillStyle = c; ctx.font = 'bold 12px sans-serif';
        ctx.fillText(label, pl[0][0] * W + 5, pl[0][1] * H - 5);
      }
    };
    poly(geo.shop, '#38bdf8', 'Shop floor');
    (geo.staff || []).forEach((z, i) => poly(z, '#a855f7', `Staff ${i + 1}`));
    poly(geo.counter, '#f59e0b', 'Behind counter');
    poly(geo.service, '#fb7185', 'At the counter');
    (geo.shelf || []).forEach((z, i) => poly(z.poly || z, '#facc15',
      `Shelf ${i + 1}${(z.open_at != null) ? ` · open @${Math.round(z.open_at)}s` : ''}`));

    if (geo.line) {
      const [a, b] = geo.line;
      const ax = a[0] * W, ay = a[1] * H, bx = b[0] * W, by = b[1] * H;
      ctx.strokeStyle = '#22c55e'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
      dot(a, '#22c55e'); dot(b, '#22c55e');
      // Arrow to the side that counts as IN — the same perpendicular sign the
      // backend uses, flipped by in_side. "Which way is in" is the one thing about
      // a counting line nobody can infer from looking at it.
      let nx = -(by - ay), ny = (bx - ax);
      const L = Math.hypot(nx, ny) || 1; nx /= L; ny /= L;
      if (geo.in_side < 0) { nx = -nx; ny = -ny; }
      const mx = (ax + bx) / 2, my = (ay + by) / 2, len = 36;
      const tx = mx + nx * len, ty = my + ny * len;
      ctx.beginPath(); ctx.moveTo(mx, my); ctx.lineTo(tx, ty); ctx.stroke();
      const ang = Math.atan2(ty - my, tx - mx);
      ctx.fillStyle = '#22c55e';
      ctx.beginPath(); ctx.moveTo(tx, ty);
      ctx.lineTo(tx - 9 * Math.cos(ang - 0.4), ty - 9 * Math.sin(ang - 0.4));
      ctx.lineTo(tx - 9 * Math.cos(ang + 0.4), ty - 9 * Math.sin(ang + 0.4));
      ctx.closePath(); ctx.fill();
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText('IN', tx + nx * 12 - 6, ty + ny * 12 + 4);
    }

    if (pts.length) {
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 2;
      ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.stroke();
      if (spec.shape === 'poly' && pts.length >= 3) {
        ctx.save(); ctx.setLineDash([5, 4]); ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(pts[pts.length - 1][0] * W, pts[pts.length - 1][1] * H);
        ctx.lineTo(pts[0][0] * W, pts[0][1] * H); ctx.stroke(); ctx.restore();
      }
      pts.forEach((p, i) => {
        dot(p, '#f59e0b');
        ctx.fillStyle = '#1f2937'; ctx.font = 'bold 10px sans-serif';
        ctx.fillText(String(i + 1), p[0] * W - 3, p[1] * H + 3.5);
      });
    }
  }, [job?.id, geo, pts, spec, token]);

  const onCanvasClick = (e) => {
    if (running) return;
    const cv = canvasRef.current; if (!cv) return;
    const r = cv.getBoundingClientRect();
    const p = [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
    if (spec.shape === 'line') {
      const next = [...pts, p];
      if (next.length === 2) { setGeo(g => ({ ...g, line: next })); setPts([]); }
      else setPts(next);
      return;
    }
    setPts(prev => [...prev, p]);
  };

  const finishShape = () => {
    if (pts.length < 3) { toast('Click at least 3 points.', 'err'); return; }
    const { poly, fixed } = makeSimpleRing(pts);
    if (fixed) toast('Those corners crossed over — reordered them into a clean shape.', 'warn');
    setGeo(g => mode === 'staff' ? { ...g, staff: [...g.staff, poly] }
      : mode === 'shelf' ? { ...g, shelf: [...g.shelf, { poly, open_at: null }] }
        : mode === 'shop' ? { ...g, shop: poly }
          : mode === 'service' ? { ...g, service: poly }
            : { ...g, counter: poly });
    setPts([]);
  };

  const clearShape = () => {
    setPts([]);
    setGeo(g => mode === 'line' ? { ...g, line: null }
      : mode === 'shop' ? { ...g, shop: null }
        : mode === 'counter' ? { ...g, counter: null }
          : mode === 'service' ? { ...g, service: null }
            : mode === 'staff' ? { ...g, staff: [] } : { ...g, shelf: [] });
  };

  const run = () => {
    if (!job?.id) return;
    setBusy(true);
    apiFetch(`/ui/analyze/${job.id}/run`, {
      method: 'POST',
      json: {
        role: 'entrance',
        analysis_fps: Number(fps), max_secs: Number(maxSecs) || 0,
        entry_line: geo.line, in_side: geo.in_side, line_purpose: geo.purpose,
        shop_zone: geo.shop, staff_zones: geo.staff,
        counter_zone: geo.counter, service_zone: geo.service,
        shelf_zones: (geo.shelf || []).map((z, i) => ({
          name: `Shelf ${i + 1}`, polygon: z.poly || z, open_at: z.open_at ?? null })),
        detect_shelf: doShelf, identify_faces: doFaces,
        min_visit_secs: Number(minVisit),
      },
    }).then(r => r.json().then(d => {
      if (!r.ok) { toast(d.detail || 'Could not start.', 'err'); return; }
      setJob(d); loadJobs();
    })).catch(() => toast('Could not start the analysis.', 'err'))
      .finally(() => setBusy(false));
  };

  const cancel = () => apiFetch(`/ui/analyze/${job.id}/cancel`, { method: 'POST' })
    .then(() => toast('Stopping…', 'warn')).catch(() => {});

  const remove = (id) => {
    if (!window.confirm('Delete this video and everything the analysis produced?')) return;
    apiFetch(`/ui/analyze/${id}`, { method: 'DELETE' })
      .then(() => { if (job?.id === id) setJob(null); loadJobs(); toast('Deleted.', 'ok'); })
      .catch(() => toast('Could not delete.', 'err'));
  };

  const proofUrl = (name) =>
    `${STREAM_API}/ui/analyze/${job.id}/proof/${encodeURIComponent(name)}?token=${token}`;

  const drawn = useMemo(() => ({
    line: !!geo.line, shop: !!geo.shop, counter: !!geo.counter,
    service: !!geo.service,
    staff: (geo.staff || []).length, shelf: (geo.shelf || []).length,
  }), [geo]);

  return (
    <IndustrialLayout eyebrow="Analysis" title="Video Analyzer"
      subtitle={job
        ? `${job.filename} · ${job.video.width}×${job.video.height} · ${secs(job.video.duration)}`
          // Which run you are looking at. Without it, a report from the old code
          // and one from the new are indistinguishable on screen.
          + (job.finished ? ` · analysed ${when(job.finished)}`
            : job.created ? ` · uploaded ${when(job.created)}` : '')
        : 'UPLOAD A RECORDING TO MEASURE IT'}>

      {/* ── upload + previous runs ───────────────────────────────────────── */}
      <div className="ind-card">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="ind-eye">Recording</span>
          <input ref={fileRef} type="file" accept="video/*,.ts,.dav" style={{ display: 'none' }}
            onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
          <button className="ind-pill on" onClick={() => fileRef.current?.click()}
            disabled={!!up}>⬆ Upload video</button>
          {up && (
            <>
              <span style={{ fontSize: 12, color: 'var(--ind-tx2)', fontFamily: 'var(--ind-mono)' }}>
                {up.error ? `upload failed — ${up.error}`
                  : `uploading ${up.name} — ${up.pct}%`}
              </span>
              {!up.error && (
                <button className="ind-pill" onClick={cancelUpload}>✕ Cancel</button>
              )}
              {up.error && (
                <button className="ind-pill" onClick={() => { _up = null; _upEmit(); }}>
                  Dismiss
                </button>
              )}
            </>
          )}
          {jobs.length > 0 && (
            <select value={job?.id || ''} onChange={e => e.target.value && openJob(e.target.value)}
              style={sel}>
              <option value="">Previous analyses…</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>
                  {when(j.finished || j.created)} · {j.filename} — {j.status}
                  {j.summary ? ` · ${j.summary.unique_customers} customers` : ''}
                </option>
              ))}
            </select>
          )}
          {/* Manual re-read. Auto-polling only runs while a job is working, and it
              stops the moment one finishes — so without this there is no way to
              re-check after a backgrounded tab, a dropped request, or a job started
              from another browser. */}
          <button className="ind-pill" onClick={refresh} title="Re-read from the server">
            ↻ Refresh
          </button>
          {job && <button className="ind-pill" onClick={() => remove(job.id)}>🗑 Delete</button>}
        </div>
        {listErr && (
          <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, fontSize: 13,
            border: '1px solid var(--ind-bad, #ef4444)', color: 'var(--ind-bad, #ef4444)' }}>
            {listErr}
          </div>
        )}
        {!job && (
          <div className="ind-empty" style={{ padding: 24, lineHeight: 1.7, marginTop: 12 }}>
            <b>Upload a recording from the shop.</b><br />
            Best results come from the camera that sees the door and the floor in one
            frame. Move the NVR export as a <b>file</b> — “send as document”, Drive or
            a USB stick. Sent through a messaging app it gets re-encoded to a fraction
            of the camera’s resolution however good the export was, and every figure
            here is limited by the pixels on the person.
          </div>
        )}
      </div>

      {job && (
        <>
          {/* ── mark up the scene ──────────────────────────────────────────── */}
          {/* Maximised, this same markup block becomes a full-viewport overlay.
              The canvas maps clicks through getBoundingClientRect, so the
              coordinates stay correct at any displayed size and the shapes drawn
              big are identical to the ones drawn small. */}
          <div className={big ? '' : 'ind-card'}
            style={big ? {
              position: 'fixed', inset: 0, zIndex: 70, background: 'var(--ind-bg, #0b1020)',
              padding: 14, overflow: 'auto',
            } : undefined}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <span className="ind-eye">Mark up</span>
              {DRAW.map(d => (
                <button key={d.key} className={`ind-pill${mode === d.key ? ' on' : ''}`}
                  onClick={() => { setMode(d.key); setPts([]); }}>
                  {d.label}
                  {d.key === 'staff' && drawn.staff ? ` (${drawn.staff})` : ''}
                  {d.key === 'shelf' && drawn.shelf ? ` (${drawn.shelf})` : ''}
                  {d.key === 'line' && drawn.line ? ' ✓' : ''}
                  {d.key === 'shop' && drawn.shop ? ' ✓' : ''}
                  {d.key === 'counter' && drawn.counter ? ' ✓' : ''}
                  {d.key === 'service' && drawn.service ? ' ✓' : ''}
                </button>
              ))}
              <span style={{ flex: 1 }} />
              {spec.shape === 'poly' && <button className="ind-pill" onClick={finishShape}>✓ Finish shape</button>}
              {mode === 'line' && geo.line && (
                <button className="ind-pill" onClick={() => setGeo(g => ({ ...g, in_side: -g.in_side }))}>
                  ⇄ Swap in / out
                </button>
              )}
              <button className="ind-pill" onClick={clearShape}>✕ Clear</button>
              <button className={`ind-pill${big ? ' on' : ''}`} onClick={() => setBig(b => !b)}
                title={big ? 'Back to the page (Esc)' : 'Draw at full screen'}>
                {big ? '⤡ Exit full screen' : '⛶ Full screen'}
              </button>
            </div>
            <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '0 0 10px', lineHeight: 1.6 }}>
              {spec.hint}
            </p>

            {/* What the line means is decided by where it was drawn, and only the
                person drawing it knows. Asking is the difference between "12 people
                entered the shop" and "12 customers were served". */}
            {mode === 'line' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: 'var(--ind-tx3)' }}>This line is across the</span>
                <button className={`ind-pill${geo.purpose === 'entrance' ? ' on' : ''}`}
                  onClick={() => setGeo(g => ({ ...g, purpose: 'entrance' }))}>
                  🚪 Doorway — entering the shop
                </button>
                <button className={`ind-pill${geo.purpose === 'counter' ? ' on' : ''}`}
                  onClick={() => setGeo(g => ({ ...g, purpose: 'counter' }))}>
                  🧾 Counter — stepping up to be served
                </button>
              </div>
            )}

            {/* Each drawn shelf, and the moment it was marked OPEN. Without a
                marked moment the detector falls back to "something changed here",
                which an arm passing over satisfies just as well as an open drawer
                — measured, that read as open for 112s of a 127s clip. */}
            {mode === 'shelf' && (geo.shelf || []).length > 0 && (
              <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 8,
                border: '1px solid var(--ind-line)', background: 'var(--ind-ink)' }}>
                {geo.shelf.map((z, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8,
                    flexWrap: 'wrap', marginBottom: 4, fontSize: 12 }}>
                    <span style={{ color: '#facc15', minWidth: 56 }}>Shelf {i + 1}</span>
                    <span style={{ color: 'var(--ind-tx3)' }}>
                      {z.open_at != null
                        ? `open state marked at ${secs(z.open_at)}`
                        : 'open state not marked — will fall back to detecting change'}
                    </span>
                    <button className="ind-pill" disabled={running}
                      onClick={() => setGeo(g => ({
                        ...g,
                        shelf: g.shelf.map((x, j) => j === i
                          ? { ...x, open_at: (frameT ?? 2) } : x),
                      }))}>
                      ⌖ Mark open at {secs(frameT ?? 2)}
                    </button>
                    {z.open_at != null && (
                      <button className="ind-pill" disabled={running}
                        onClick={() => setGeo(g => ({
                          ...g,
                          shelf: g.shelf.map((x, j) => j === i ? { ...x, open_at: null } : x),
                        }))}>✕</button>
                    )}
                  </div>
                ))}
                <div style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 4, lineHeight: 1.5 }}>
                  Scrub to a frame where the shelf is open, then mark it. The clip is
                  matched against how it looks there, which is a much narrower claim
                  than “something changed”.
                </div>
              </div>
            )}

            {/* Scrub the clip. A shelf has to be marked at a moment it is OPEN,
                and that moment is wherever it happens to be in the recording —
                without this the page could only ever show the still at 2s. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
              flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--ind-tx3)', whiteSpace: 'nowrap' }}>
                FRAME AT
              </span>
              <input type="range" min={0} max={Math.max(1, Math.floor(job.video.duration))}
                step={1} value={frameT ?? 2} disabled={running}
                onChange={e => setFrameT(Number(e.target.value))}
                style={{ flex: '1 1 260px', minWidth: 180 }} />
              <span style={{ fontSize: 12, fontFamily: 'var(--ind-mono)', color: 'var(--ind-tx2)',
                minWidth: 64 }}>
                {secs(frameT ?? 2)}
              </span>
              {frameT != null && (
                <button className="ind-pill" onClick={() => setFrameT(null)}>↺ Default frame</button>
              )}
            </div>

            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden',
              border: '1px solid var(--ind-line)',
              ...(big ? { display: 'inline-block', maxWidth: 'none' } : { maxWidth: 900 }) }}>
              <img ref={imgRef} alt="" src={token
                ? `${STREAM_API}/ui/analyze/${job.id}/frame?token=${token}`
                  + (frameT != null ? `&t=${frameT}` : '')
                : undefined}
                style={big
                  ? { display: 'block', maxHeight: 'calc(100vh - 150px)', maxWidth: 'calc(100vw - 40px)', width: 'auto' }
                  : { width: '100%', display: 'block' }}
                onLoad={e => {
                  const cv = canvasRef.current;
                  if (cv) { cv.width = e.target.naturalWidth; cv.height = e.target.naturalHeight; }
                  setPts(p => [...p]);          // force one redraw now the size is known
                }} />
              <canvas ref={canvasRef} onClick={onCanvasClick}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                  cursor: running ? 'not-allowed' : 'crosshair' }} />
            </div>
          </div>

          {/* ── run ────────────────────────────────────────────────────────── */}
          <div className="ind-card">
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Field label="Frames per second analysed"
                hint="8 is the tested default. Below about 6 the tracker starts losing people through occlusions and misses their exit — measured, not guessed.">
                <select value={fps} onChange={e => setFps(Number(e.target.value))} style={sel} disabled={running}>
                  {[2, 3, 4, 6, 8, 10, 12].map(v => <option key={v} value={v}>{v} fps</option>)}
                </select>
              </Field>
              <Field label="Analyse only the first"
                hint="A quick look at a long recording before committing to the whole thing.">
                <select value={maxSecs} onChange={e => setMax(Number(e.target.value))} style={sel} disabled={running}>
                  <option value={0}>Whole video</option>
                  {[60, 120, 300, 600, 1800].map(v => <option key={v} value={v}>{secs(v)}</option>)}
                </select>
              </Field>
              <Field label="Ignore presences under"
                hint="Shorter than this is a passer-by or a detection blink, not a visit.">
                <select value={minVisit} onChange={e => setMinV(Number(e.target.value))} style={sel} disabled={running}>
                  {[0, 2, 3, 5, 10].map(v => <option key={v} value={v}>{v ? `${v}s` : 'nothing'}</option>)}
                </select>
              </Field>
              <button className={`ind-pill${doShelf ? ' on' : ''}`} onClick={() => setShelf(s => !s)} disabled={running}>
                {doShelf ? '☑' : '☐'} Shelf openings
              </button>
              <button className={`ind-pill${doFaces ? ' on' : ''}`} onClick={() => setFaces(s => !s)} disabled={running}>
                {doFaces ? '☑' : '☐'} Name staff by face
              </button>
              <span style={{ flex: 1 }} />
              {running
                ? <button className="ind-pill" onClick={cancel}>■ Stop</button>
                : <button className="ind-pill on" onClick={run} disabled={busy}>▶ Run analysis</button>}
            </div>

            {!drawn.line && !running && (
              <p style={{ fontSize: 12, color: 'var(--ind-warn, #f59e0b)', margin: '10px 0 0' }}>
                No entrance line drawn — it will report unique visitors and time spent,
                but not in vs out.
              </p>
            )}

            {running && (
              <div style={{ marginTop: 14 }}>
                <div style={{ height: 8, borderRadius: 4, background: 'var(--ind-ink)',
                  border: '1px solid var(--ind-line)', overflow: 'hidden' }}>
                  <div style={{ width: `${job.progress?.pct || 0}%`, height: '100%',
                    background: 'var(--ind-ok)', transition: 'width .4s' }} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--ind-tx2)', marginTop: 6,
                  fontFamily: 'var(--ind-mono)' }}>
                  {job.progress?.pct || 0}% · {job.progress?.stage || 'starting'}
                  {job.progress?.eta_secs != null && ` · about ${secs(job.progress.eta_secs)} left`}
                  {/* NOT a headcount. This is raw tracks — fragments of one person,
                      staff and carried children all included — and calling it
                      "people" made a mid-run 16 look like a contradiction of a
                      final answer of 2. */}
                  {job.progress?.tracks != null && ` · ${job.progress.tracks} sightings so far (not a headcount)`}
                </div>

                {/* Watch it work: the frame being analysed, with the zones and the
                    tracked people drawn on it, and the tallies as they stand.
                    Explicitly PROVISIONAL — staff and carried people are only
                    separated at the end, so a crossing counted here can still be
                    withdrawn from the final total. */}
                <div style={{ display: 'flex', gap: 14, marginTop: 14, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 420px', minWidth: 280 }}>
                    {liveUrl
                      ? <img src={liveUrl} alt="" style={{ width: '100%', display: 'block',
                          borderRadius: 8, border: '1px solid var(--ind-line)' }} />
                      : <div className="ind-empty" style={{ padding: 30 }}>
                          Waiting for the first analysed frame…
                        </div>}
                  </div>
                  <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Stat label="CROSSED IN"  val={job.progress?.crossings_in ?? 0}  color="var(--ind-ok)" />
                      <Stat label="CROSSED OUT" val={job.progress?.crossings_out ?? 0} color="var(--ind-accent)" />
                      <Stat label="ON STAFF SIDE" val={job.progress?.staff_so_far ?? 0} color="#a855f7" />
                      {job.progress?.shelf_events > 0 &&
                        <Stat label="SHELF OPENINGS" val={job.progress.shelf_events} color="#facc15" />}
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 8, lineHeight: 1.6 }}>
                      Provisional. Staff and carried people are separated out at the
                      end, so these can still go down — a crossing by someone who
                      turns out to work here is withdrawn from the final count.
                    </p>
                    {(job.progress?.latest || []).length > 0 && (
                      <>
                        <span className="ind-eye">Latest crossings</span>
                        <div style={{ marginTop: 6 }}>
                          {job.progress.latest.map(n => (
                            <Thumb key={n} src={proofUrl(n)} small
                              onClick={() => setLightbox({ src: proofUrl(n), caption: n })} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            {job.status === 'error' && (
              <p style={{ fontSize: 13, color: 'var(--ind-bad, #ef4444)', margin: '10px 0 0' }}>
                Analysis failed: {job.error}
              </p>
            )}
          </div>

          {/* ── report ─────────────────────────────────────────────────────── */}
          {results && <Report res={results} job={job} proofUrl={proofUrl}
            onProof={setLightbox} token={token} />}
        </>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.82)', zIndex: 60,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 10, padding: 20, cursor: 'zoom-out' }}>
          <img src={lightbox.src} alt="" style={{ maxWidth: '94%', maxHeight: '84%',
            borderRadius: 8, border: '1px solid #333' }} />
          <div style={{ color: '#e5e7eb', fontSize: 13 }}>{lightbox.caption}</div>
        </div>
      )}
    </IndustrialLayout>
  );
}

// ── the report ────────────────────────────────────────────────────────────────

